import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, TrendingUp, Globe, Heart } from 'lucide-react';
import { useStoreData } from '../hooks/useStoreData';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Cart from '../components/Cart';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const OurStory = () => {
  const navigate = useNavigate();
  const { settings } = useStoreData();
  const { getCartCount } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);

  const milestones = [
    {
      year: "2025",
      title: "The Beginning",
      description: "Founded with a vision to provide elegant modest fashion to women who value both tradition and style."
    },
    {
      year: "1950s",
      title: "Building Trust",
      description: "Established ourselves as a trusted name in the community, known for quality craftsmanship and attention to detail."
    },
    {
      year: "1980s",
      title: "Expanding Horizons",
      description: "Grew our collection to include a wider variety of styles, fabrics, and designs to suit every occasion."
    },
    {
      year: "2000s",
      title: "Modern Evolution",
      description: "Embraced contemporary designs while maintaining our commitment to traditional values and quality."
    },
    {
      year: "2020",
      title: "Digital Transformation",
      description: "Launched our online store to serve customers across Pakistan with the same care and quality they've trusted for generations."
    },
    {
      year: "Today",
      title: "Continuing Legacy",
      description: "A proud legacy of almost a century, serving thousands of satisfied customers with unwavering dedication to excellence."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Our Story - Heritage Since 2025 | Rizzahra"
        description="Discover Rizzahra's journey from 2025 to today. Almost a century of excellence in modest fashion, serving thousands with quality abayas and Islamic clothing."
        keywords="rizzahra history, heritage, modest fashion legacy, abaya tradition, islamic clothing"
        url="/our-story"
      />
      {/* Main Header */}
      <Header
        cartCount={getCartCount()}
        onCartClick={() => setIsCartOpen(true)}
        onAdminClick={() => navigate('/admin')}
      />

      {/* Page Header */}
      <header className="bg-[#4a1a1f] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold uppercase tracking-wider">Our Story</h1>
          <p className="text-gray-300 mt-2">A legacy of elegance since 2025</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-16 text-center">
          <div className="inline-block bg-[#4a1a1f] text-white px-6 py-2 rounded-full mb-6 uppercase tracking-wider text-sm font-semibold">
            Since 2025
          </div>
          <h2 className="text-4xl font-bold text-[#4a1a1f] mb-6">
            Nearly a Century of Excellence
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            What started as a small family endeavor in 2025 has blossomed into one of Pakistan's most
            trusted names in modest fashion. Our story is one of dedication, passion, and an unwavering
            commitment to serving women who value elegance and modesty.
          </p>
        </div>

        {/* The Journey */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-[#4a1a1f] text-center mb-12 uppercase tracking-wider">
            Our Journey Through Time
          </h2>
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#4a1a1f] opacity-20"></div>

            {/* Timeline Items */}
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row gap-8 items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="flex-1"></div>
                  <div className="hidden md:flex items-center justify-center w-16 h-16 rounded-full bg-[#4a1a1f] text-white font-bold text-lg z-10 flex-shrink-0">
                    {milestone.year === "Today" ? <Heart size={24} /> : milestone.year.slice(-2)}
                  </div>
                  <div className="flex-1 bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="text-[#4a1a1f] font-bold text-2xl mb-2">{milestone.year}</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{milestone.title}</h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Our Vision */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-[#4a1a1f] to-[#5a2a2f] text-white rounded-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={32} />
              <h2 className="text-3xl font-bold uppercase tracking-wider">Our Vision</h2>
            </div>
            <p className="text-gray-200 text-lg leading-relaxed mb-4">
              To be the leading destination for modest fashion in Pakistan and beyond, where every woman
              can find abayas that make her feel confident, beautiful, and true to her values.
            </p>
            <p className="text-gray-200 text-lg leading-relaxed">
              We envision a world where modest fashion is celebrated, accessible, and synonymous with
              elegance and quality—and we're committed to making that vision a reality, one beautiful
              abaya at a time.
            </p>
          </div>
        </div>

        {/* What Drives Us */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-[#4a1a1f] text-center mb-10 uppercase tracking-wider">
            What Drives Us
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                  <Heart className="text-white" size={28} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Passion for Excellence</h3>
              <p className="text-gray-600">
                Every stitch, every fabric choice, every design reflects our passion for creating
                exceptional modest fashion.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                  <TrendingUp className="text-white" size={28} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Continuous Innovation</h3>
              <p className="text-gray-600">
                We constantly evolve with changing trends while staying true to the timeless principles
                of modest fashion.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                  <Globe className="text-white" size={28} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Community Impact</h3>
              <p className="text-gray-600">
                Supporting local artisans, creating jobs, and giving back to the community that has
                supported us for generations.
              </p>
            </div>
          </div>
        </div>

        {/* Closing Statement */}
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Thank You for Being Part of Our Story</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              As we look forward to the future, we're excited to continue this journey with you.
              Your trust, loyalty, and feedback inspire us every day to be better, to do better,
              and to serve you better. Here's to many more years of elegance, quality, and shared values.
            </p>
          </div>
        </div>
      </div>

      <Footer />
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default OurStory;
